// Premium collections (schema 9, idea 30): overdue or failed premiums per policy. The real problem for an advisor:
// the insurer's overdue list arrives as a spreadsheet, the client does not know a debit failed, and a policy lapses
// quietly a few weeks later. Here every overdue item is recorded (by hand or from the insurer's list), gets a
// follow-up task and a polite reminder DRAFT for the client (nothing is sent automatically), shows "risk of lapse"
// on the policy when it stays open longer than a set number of days (Settings, default 15), and is closed as paid or
// lapsed — never deleted.
//
// Import rules: the advisor maps the columns (policy number, amount, due date); a row is matched to a policy ONLY by
// an exact policy number (case and spaces ignored). No match, two matches, an unreadable amount or date → the row is
// shown as unmatched with the reason, never guessed. A row already recorded (same policy, due date and amount) is
// skipped, so importing the same list twice records nothing new.
import { stableId } from './ids.ts';
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { DomainError, livePolicy, created, opt, put, timeline, touched, type Ctx } from './cmd.ts';
import { diffDays, normalizeDay, type Day } from './dates.ts';
import { parseAmount } from './extraction.ts';
import { roundMoney } from './money.ts';
import { currencyOf } from './policy-engine.ts';
import { searchKey } from './rules.ts';
import { getSetting } from './services.ts';
import type { Policy, PremiumArrear, PremiumArrearSource, Task } from './schema.ts';

/** days an overdue premium may stay open before the policy shows "risk of lapse" (Settings `premiums.lapseRiskDays`) */
export const DEFAULT_LAPSE_RISK_DAYS = 15;
export const lapseRiskDays = (v: WorkspaceView): number => {
  const n = Number(getSetting<number>(v, 'premiums.lapseRiskDays', DEFAULT_LAPSE_RISK_DAYS));
  return Number.isInteger(n) && n >= 1 && n <= 365 ? n : DEFAULT_LAPSE_RISK_DAYS;
};

export interface PremiumArrearInput { id?: string; policyId: string; amount: number | string | null; dueDate: string | null; note?: string | null; source?: PremiumArrearSource; policyNumber?: string | null; importRef?: string | null }
/** follow-up task title (the UI passes translated text; the default is English) */
export type ArrearTaskTitle = (policy: Policy, a: PremiumArrear) => string;
const defaultTitle: ArrearTaskTitle = (p) => `Overdue premium: ${p.productName}${p.policyNumber ? ` (${p.policyNumber})` : ''}`;

/** the deterministic id of an item's follow-up (two devices create the same task, never two) */
export const arrearTaskId = (arrearId: string) => stableId('premium_arrear_task', arrearId);
const taskOf = (t: Task) => (t.data ?? {}) as { arrearId?: string };

function amountOf(x: unknown): number {
  const n = typeof x === 'number' ? x : x == null || x === '' ? null : parseAmount(String(x));
  if (n === null || !Number.isFinite(n) || n <= 0) throw new DomainError('premium.amount');
  return n;
}

function followUp(ctx: Ctx, p: Policy, a: PremiumArrear, title: ArrearTaskTitle): Put {
  return put('tasks', created<'tasks'>(ctx, {
    id: arrearTaskId(a.id), kind: 'followup', title: title(p, a).slice(0, 160), personId: a.personId, prospectId: null, caseId: null, policyId: p.id,
    dueDate: ctx.today, dueTime: null, durationMin: null, locationType: null, location: null, status: 'open', priority: 'high', notes: a.note ?? null,
    origin: 'premium_arrear', data: { arrearId: a.id, dueDate: a.dueDate, amount: a.amount },
  }));
}

/** record an overdue / failed premium (or correct an open one) — with its follow-up task and a timeline entry */
export function savePremiumArrear(v: WorkspaceView, input: PremiumArrearInput, ctx: Ctx, title: ArrearTaskTitle = defaultTitle): Changeset {
  const cur = input.id ? v.get('premium_arrears', input.id) : undefined;
  if (input.id && !cur) throw new DomainError('premium.not_found');
  if (cur && cur.status !== 'open') throw new DomainError('premium.closed');
  const p = livePolicy(v, cur?.policyId ?? input.policyId);
  if (cur && input.policyId && input.policyId !== cur.policyId) throw new DomainError('premium.policy_fixed');
  const amount = roundMoney(amountOf(input.amount), currencyOf(p, ctx.region));
  const dueDate = normalizeDay(input.dueDate);
  if (!dueDate) throw new DomainError('premium.due_date');
  if (dueDate > ctx.today) throw new DomainError('premium.future');
  if (!cur && v.where('premium_arrears', 'policyId', p.id).some((x) => x.status === 'open' && x.dueDate === dueDate)) throw new DomainError('premium.duplicate');
  const fields = { amount, dueDate, note: input.note !== undefined ? opt(input.note) : cur?.note ?? null };
  const row: PremiumArrear = cur ? touched(ctx, cur, fields) : created<'premium_arrears'>(ctx, {
    policyId: p.id, personId: p.personId, currency: currencyOf(p, ctx.region), status: 'open', source: input.source ?? 'manual', resolvedAt: null,
    policyNumber: opt(input.policyNumber) ?? p.policyNumber ?? null, importRef: opt(input.importRef), ...fields,
  });
  const puts: Put[] = [put('premium_arrears', row)];
  if (!cur) {
    if (!v.get('tasks', arrearTaskId(row.id))) puts.push(followUp(ctx, p, row, title));
    puts.push(timeline(ctx, row.personId, 'policy', 'timeline.premium_overdue', `${p.productName} · ${row.amount.toFixed(2)} ${row.currency ?? ''} · ${row.dueDate}`.trim(), { table: 'policies', id: p.id }));
  }
  return { action: cur ? 'premium.update' : 'premium.record', entity: 'premium_arrears', entityId: row.id, summary: p.productName, puts, deletes: [] };
}

/** close an item as paid or lapsed (kept, never deleted); its open follow-up is completed */
export function resolvePremiumArrear(v: WorkspaceView, id: string, status: 'paid' | 'lapsed', ctx: Ctx, note?: string | null): Changeset {
  const a = v.get('premium_arrears', id);
  if (!a) throw new DomainError('premium.not_found');
  if (status !== 'paid' && status !== 'lapsed') throw new DomainError('premium.status');
  if (a.status !== 'open') throw new DomainError('premium.closed');
  const p = v.get('policies', a.policyId);
  const row = touched(ctx, a, { status, resolvedAt: ctx.now, note: [a.note, opt(note)].filter(Boolean).join('\n') || null });
  const puts: Put[] = [put('premium_arrears', row)];
  for (const t of v.rows('tasks')) if (t.status === 'open' && t.origin === 'premium_arrear' && taskOf(t).arrearId === a.id) puts.push(put('tasks', touched(ctx, t, { status: 'done', completedAt: ctx.now })));
  puts.push(timeline(ctx, a.personId, 'policy', status === 'paid' ? 'timeline.premium_paid' : 'timeline.premium_lapsed', p?.productName ?? null, { table: 'policies', id: a.policyId }));
  return { action: `premium.${status}`, entity: 'premium_arrears', entityId: a.id, summary: p?.productName, puts, deletes: [] };
}

export interface OpenArrear { arrear: PremiumArrear; policy: Policy; days: number; atRisk: boolean }
/** open items (person not archived), oldest due date first, each with the days it has been open and the lapse-risk flag */
export function openArrears(v: WorkspaceView, today: Day, opts: { personId?: string; policyId?: string } = {}): OpenArrear[] {
  const limit = lapseRiskDays(v);
  const rows = opts.policyId ? v.where('premium_arrears', 'policyId', opts.policyId) : opts.personId ? v.where('premium_arrears', 'personId', opts.personId) : v.rows('premium_arrears');
  const out: OpenArrear[] = [];
  for (const a of rows) {
    if (a.status !== 'open') continue;
    const policy = v.get('policies', a.policyId);
    const person = v.get('persons', a.personId);
    if (!policy || !person || person.archivedAt) continue;
    const days = Math.max(0, diffDays(a.dueDate, today));
    out.push({ arrear: a, policy, days, atRisk: days > limit });
  }
  return out.sort((x, y) => x.arrear.dueDate.localeCompare(y.arrear.dueDate) || x.policy.productName.localeCompare(y.policy.productName));
}
/** the policy-card summary: open items, total open, the longest open and whether the policy is at risk of lapse */
export function policyArrears(v: WorkspaceView, policyId: string, today: Day): { open: OpenArrear[]; total: number; maxDays: number; atRisk: boolean } {
  const open = openArrears(v, today, { policyId });
  return { open, total: roundMoney(open.reduce((s, x) => s + x.arrear.amount, 0)), maxDays: Math.max(0, ...open.map((x) => x.days)), atRisk: open.some((x) => x.atRisk) };
}

// --- import of an insurer's overdue list ------------------------------------------------------------------------
export const ARREAR_IMPORT_FIELDS = ['policyNumber', 'amount', 'dueDate'] as const;
export type ArrearImportField = typeof ARREAR_IMPORT_FIELDS[number];
export type ArrearMapping = Partial<Record<ArrearImportField, number>>;
const HEADER_HINTS: Record<ArrearImportField, RegExp> = {
  policyNumber: /policy|polic|συμβολ|αρ\.? ?συμβ|contract/,
  amount: /amount|premium|due amount|outstanding|ποσο|ασφαλιστρ|οφειλ/,
  dueDate: /due|date|ημερομ|ληξη|λήξη|προθεσμ/,
};
/** a first guess of the column mapping from the header row (the advisor confirms or changes it) */
export function guessArrearMapping(header: readonly string[]): ArrearMapping {
  const out: ArrearMapping = {};
  const used = new Set<number>();
  for (const f of ARREAR_IMPORT_FIELDS) {
    const i = header.findIndex((h, idx) => !used.has(idx) && HEADER_HINTS[f].test(searchKey(h)));
    if (i >= 0) { out[f] = i; used.add(i); }
  }
  return out;
}
/** policy numbers compare without case and spaces — nothing more (no guessing on similar numbers) */
export const policyNumberKey = (s: string | null | undefined) => String(s ?? '').replace(/\s+/g, '').toUpperCase();

export type UnmatchedReason = 'no_number' | 'no_policy' | 'ambiguous' | 'amount' | 'date' | 'future';
export interface ArrearImportRow { line: number; policyNumber: string; amountText: string; dateText: string }
export interface MatchedArrearRow extends ArrearImportRow { policy: Policy; amount: number; dueDate: Day; duplicate: boolean }
export interface UnmatchedArrearRow extends ArrearImportRow { reason: UnmatchedReason }
export interface ArrearImportPreview { matched: MatchedArrearRow[]; unmatched: UnmatchedArrearRow[]; duplicates: number }

/** match the list's rows to policies (nothing is written). `rows` include the header when `hasHeader`. */
export function previewArrearImport(v: WorkspaceView, rows: readonly (readonly string[])[], mapping: ArrearMapping, today: Day, opts: { hasHeader?: boolean } = {}): ArrearImportPreview {
  if (mapping.policyNumber == null || mapping.amount == null || mapping.dueDate == null) throw new DomainError('premium.mapping');
  const byNumber = new Map<string, Policy[]>();
  for (const p of v.rows('policies')) { const k = policyNumberKey(p.policyNumber); if (k) byNumber.set(k, [...(byNumber.get(k) ?? []), p]); }
  const out: ArrearImportPreview = { matched: [], unmatched: [], duplicates: 0 };
  const seen = new Set<string>();
  rows.forEach((r, i) => {
    if (opts.hasHeader !== false && i === 0) return;
    const base: ArrearImportRow = { line: i + 1, policyNumber: String(r[mapping.policyNumber!] ?? '').trim(), amountText: String(r[mapping.amount!] ?? '').trim(), dateText: String(r[mapping.dueDate!] ?? '').trim() };
    if (!base.policyNumber && !base.amountText && !base.dateText) return;
    const miss = (reason: UnmatchedReason) => out.unmatched.push({ ...base, reason });
    if (!policyNumberKey(base.policyNumber)) return miss('no_number');
    const pols = byNumber.get(policyNumberKey(base.policyNumber)) ?? [];
    if (!pols.length) return miss('no_policy');
    if (pols.length > 1) return miss('ambiguous');
    const amount = base.amountText ? parseAmount(base.amountText) : null;
    if (amount === null || !(amount > 0)) return miss('amount');
    const dueDate = normalizeDay(base.dateText);
    if (!dueDate) return miss('date');
    if (dueDate > today) return miss('future');
    const policy = pols[0]!;
    const key = `${policy.id}:${dueDate}:${roundMoney(amount)}`;
    const duplicate = seen.has(key) || v.where('premium_arrears', 'policyId', policy.id).some((a) => a.dueDate === dueDate && roundMoney(a.amount) === roundMoney(amount));
    seen.add(key);
    if (duplicate) out.duplicates++;
    out.matched.push({ ...base, policy, amount: roundMoney(amount), dueDate, duplicate });
  });
  return out;
}

/** record the matched, non-duplicate rows (source 'import') in one changeset, each with its follow-up */
export function importPremiumArrears(v: WorkspaceView, rows: readonly MatchedArrearRow[], ctx: Ctx, opts: { importRef?: string | null; title?: ArrearTaskTitle } = {}): Changeset {
  const puts: Put[] = [];
  let count = 0;
  const done = new Set<string>();
  for (const r of rows) {
    if (r.duplicate) continue;
    const key = `${r.policy.id}:${r.dueDate}`;
    // an open item for the same policy and due date already exists: the insurer's figure is not applied over it
    if (done.has(key) || v.where('premium_arrears', 'policyId', r.policy.id).some((a) => a.status === 'open' && a.dueDate === r.dueDate)) continue;
    done.add(key);
    const cs = savePremiumArrear(v, { policyId: r.policy.id, amount: r.amount, dueDate: r.dueDate, source: 'import', policyNumber: r.policyNumber, importRef: opts.importRef ?? null }, ctx, opts.title);
    puts.push(...cs.puts);
    count++;
  }
  return { action: 'premium.import', entity: 'premium_arrears', summary: String(count), puts, deletes: [] };
}
